import { EPaymentType } from '@common/enums';
import { getPaymentMethodCurrency } from '@common/helpers/payment';

import {
    PaymentMethodStatus,
    type AvailablePaymentMethods,
    type PaymentCurrencies,
    type PaymentMethod,
} from '../contracts/payment-method.contract';

export function filterActiveSupportedPaymentMethods(paymentMethods: PaymentMethod[]): PaymentMethod[] {
    const supportedTypes = Object.values(EPaymentType).filter(Number);
    return paymentMethods.filter(
        (item) => item.status === PaymentMethodStatus.active && supportedTypes.includes(item.type)
    );
}

export function findActivePaymentMethod(paymentMethods: PaymentMethod[]): PaymentMethod | null {
    return paymentMethods?.find((payment) => payment.status === PaymentMethodStatus.active) ?? null;
}

/**
 * Stripe is connected on the backend either as the legacy key-based integration (`stripe`)
 * or through Stripe Connect (`stripeConnect`), but the widget's type dropdown exposes a
 * single "Stripe" entry — `paymentsList` hides `stripe` in favour of `stripeConnect`. So an
 * element pinned to one of the two types must accept a method connected under the other.
 */
const STRIPE_TYPES: number[] = [EPaymentType.stripe, EPaymentType.stripeConnect];

export function isMatchingPaymentType(methodType: number, elementType: number): boolean {
    const method = Number(methodType);
    const element = Number(elementType);

    if (method === element) return true;

    return STRIPE_TYPES.includes(method) && STRIPE_TYPES.includes(element);
}

export function findPaymentMethodForType(paymentMethods: PaymentMethod[], type: number): PaymentMethod | null {
    return paymentMethods?.find((method) => isMatchingPaymentType(method.type, type)) ?? null;
}

export function mapAvailablePaymentMethods(paymentMethods: PaymentMethod[]): AvailablePaymentMethods {
    const availablePaymentMethods: AvailablePaymentMethods = {
        [EPaymentType.payPal]: false,
        [EPaymentType.yooMoney]: false,
        [EPaymentType.fondy]: false,
        [EPaymentType.youKassa]: false,
        [EPaymentType.roboKassa]: false,
        [EPaymentType.stripe]: false,
        [EPaymentType.mercadopago]: false,
        [EPaymentType.liqpay]: false,
        [EPaymentType.wayforpay]: false,
        [EPaymentType.flutterwave]: false,
        [EPaymentType.monobank]: false,
        [EPaymentType.cryptomus]: false,
        [EPaymentType.custom]: true,
        [EPaymentType.whitepay]: false,
        [EPaymentType.klarna]: false,
        [EPaymentType.stripeConnect]: false,
        [EPaymentType.rozetkaPay]: false,
        [EPaymentType.novaPay]: false,
    };

    if (!paymentMethods.length) {
        return availablePaymentMethods;
    }

    paymentMethods.forEach((method: PaymentMethod) => {
        if (availablePaymentMethods[method.type] === true) {
            return;
        }
        availablePaymentMethods[method.type] = method.status === PaymentMethodStatus.active;
    });

    const isStripeAvailable =
        availablePaymentMethods[EPaymentType.stripe] || availablePaymentMethods[EPaymentType.stripeConnect];
    availablePaymentMethods[EPaymentType.stripe] = isStripeAvailable;
    availablePaymentMethods[EPaymentType.stripeConnect] = isStripeAvailable;

    return availablePaymentMethods;
}

export function mapPaymentMethodCurrencies(paymentMethods: PaymentMethod[]): PaymentCurrencies {
    const currencyLabels = {} as PaymentCurrencies;

    if (!paymentMethods?.length) {
        return currencyLabels;
    }

    paymentMethods.forEach((method: PaymentMethod) => {
        switch (method.type) {
            case EPaymentType.yooMoney:
            case EPaymentType.roboKassa: {
                const currency = getPaymentMethodCurrency(method.settings);
                return (currencyLabels[method.type] = currency ? currency : 'RUB');
            }
            case EPaymentType.payPal:
            case EPaymentType.fondy:
            case EPaymentType.monobank:
            case EPaymentType.youKassa:
            case EPaymentType.stripe:
            case EPaymentType.mercadopago:
            case EPaymentType.liqpay:
            case EPaymentType.wayforpay:
            case EPaymentType.flutterwave:
            case EPaymentType.cryptomus:
            case EPaymentType.whitepay:
            case EPaymentType.klarna:
            case EPaymentType.stripeConnect:
            case EPaymentType.rozetkaPay:
            case EPaymentType.novaPay:
                return (currencyLabels[method.type] = getPaymentMethodCurrency(method.settings));
            default:
                return null;
        }
    });

    return currencyLabels;
}
